import { useEffect, useState } from 'react';
import Image from 'next/image';
import { FieldValues, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Box, BoxProps, Button, Flex, Heading, Text, useToast } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import { ContentContainer } from '~/components/container';
import { FormRadioInput } from '~/components/form-radio-input';
import { FormTelephoneInput } from '~/components/form-telephone-input';
import { FormTextInput } from '~/components/form-text-input';
import { useLeadFormEvent } from '~/features/analytics/hooks/use-lead-form-event';
import { getAnonymousIdCookie } from '~/features/cookies/utils/get-anonymous-id-cookie';
import { StandardLegalCopy } from '~/features/legal/components/standard-legal-copy';
import { phoneNumberSchema } from '~/features/phone-number/models';
import { useUser } from '~/features/user/hooks/use-user';
enum FormState {
  IDLE,
  SUBMITTING,
  SUBMITTED,
}

const formSchema = z.object({
  email: z.string().trim().nonempty().email(),
  phoneNumber: phoneNumberSchema.optional(),
  path: z.string(),
});

const SUBMIT_BUTTON_LABEL = 'Get Your Path' as const;

export type DownloadLearningPathsSectionProps = {
  paths: Array<{
    title: string;
    learningPathDocument: {
      asset: {
        url: string;
      };
    };
  }>;
} & BoxProps;

export function DownloadLearningPathsSection({ paths, ...boxProps }: DownloadLearningPathsSectionProps) {
  const { user } = useUser();
  const { sendSegmentLeadFormEvents } = useLeadFormEvent();
  const [status, setStatus] = useState<FormState>(FormState.IDLE);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      phoneNumber: '',
      path: paths[0].title,
    },
    disabled: status === FormState.SUBMITTING,
  });
  const toast = useToast();

  useEffect(() => {
    if (user) {
      reset((prev) => ({ ...prev, phone_number: user.phoneNumber || '', email: user.email || '' }), {
        keepDirtyValues: true,
      });
    }
  }, [user, reset]);

  const selectedPath = watch('path');

  async function handleFormSubmit(fieldValues: FieldValues) {
    setStatus(FormState.SUBMITTING);
    try {
      await sendSegmentLeadFormEvents({
        identifyTraits: { email: fieldValues.email, phone_number: fieldValues.phoneNumber || undefined },
        trackAnalyticsEvent: {
          name: 'Marketing Qualified Lead Form Submitted',
          type: 'submit',
          label: `Lead Generation - HQ - Skill Path - ${fieldValues.path}`,
          action: 'form_submitted',
          additionalAttributes: {
            email: fieldValues.email,
            userIdentifier: user?.id ?? getAnonymousIdCookie()!,
          },
        },
      });
      const targetResourceUrl = paths.find((path) => path.title === fieldValues.path)?.learningPathDocument?.asset?.url;
      if (targetResourceUrl) {
        window.open(targetResourceUrl, '_blank');
        toast({
          variant: 'success',
          title: 'Success!',
          description: 'Check your email or text message inbox for a copy of the learning path.',
          duration: null,
        });
      }
      setStatus(FormState.SUBMITTED);
    } catch (error) {
      toast({
        title: '🤔 Something went wrong.',
        description: 'An error occurred submitting the form. Please try again later.',
        variant: 'error',
      });
      setStatus(FormState.IDLE);
    }
  }

  return (
    <Box
      as='section'
      bgGradient='linear(180deg, accent.sky.100 0%, #F2F6FF 50%, white 100%)'
      data-testid='learning-paths-section'
      overflow='hidden'
      position='relative'
      padding={{
        base: '40px 0px',
        xl: '80px 0px',
      }}
      {...boxProps}
    >
      <LinesBackground />
      <ContentContainer position='relative'>
        <Box mb={8} textAlign='center'>
          <Heading
            as='h2'
            mb={{
              base: 2,
              md: 0,
            }}
            size={{
              base: 'h3',
              md: 'h1',
            }}
            whiteSpace={{
              base: 'pre-wrap',
              md: 'normal',
            }}
          >
            {`Your Path to\nCareer Success`}
          </Heading>
          <Text>Chart your path to a $200k+ career in tech</Text>
        </Box>
        <Flex
          alignItems={{ lg: 'center' }}
          flexDir={{
            base: 'column',
            lg: 'row',
          }}
          gap={{
            base: 8,
            lg: 16,
          }}
        >
          <Box
            flex={{
              lg: '0 1 416px',
            }}
          >
            <Box as='form' display='flex' flexDir='column' gap={4} onSubmit={handleSubmit(handleFormSubmit)}>
              <FormRadioInput
                label={paths.length > 1 ? 'Select a path:' : 'Path:'}
                name='path'
                register={register}
                value={selectedPath}
                options={paths.map((path) => ({
                  label: path.title,
                  value: path.title,
                }))}
              />
              <FormTextInput
                label='Email me at:'
                {...register('email')}
                errorMessage={errors?.email?.message}
                type='email'
              />
              <FormTelephoneInput
                control={control}
                errorMessage={errors?.phoneNumber?.message}
                label='Text me at:'
                name='phoneNumber'
                placeholder='Phone Number'
                type='tel'
              />
              <Button
                isLoading={status === FormState.SUBMITTING}
                type='submit'
                width={{
                  base: '100%',
                  lg: 'min-content',
                }}
              >
                {SUBMIT_BUTTON_LABEL}
              </Button>
              <StandardLegalCopy submitButtonLabel={SUBMIT_BUTTON_LABEL} />
            </Box>
          </Box>
          <Box
            flex={{
              lg: '0 1 610px',
            }}
          >
            <Image
              alt=''
              height={328}
              src='/images/learning-paths.jpg'
              width={500}
              style={{
                margin: '0 auto',
                borderRadius: '8px',
                pointerEvents: 'none',
              }}
            />
          </Box>
        </Flex>
      </ContentContainer>
    </Box>
  );
}

function LinesBackground() {
  return (
    <Box
      left='50%'
      opacity={0.5}
      pointerEvents='none'
      position='absolute'
      top='50%'
      transform='translate(-50%, -50%)'
      display={{
        base: 'none',
        md: 'block',
      }}
    >
      <Box height='900px' position='relative' width='2160px'>
        <Image
          alt=''
          quality={100}
          sizes='2160px'
          src='/images/backgrounds/background-lines-light.svg'
          style={{
            objectFit: 'cover',
            objectPosition: 'left',
          }}
          fill
        />
      </Box>
    </Box>
  );
}
